import 'requestIdleCallback'
import { ProductLoader, VariantPicker, ProductStickyBar } from 'theme'
import { GWElement } from 'elements'
import { debounce, handle, parseAttribute, imagesLoaded, parseGID, sleep, fadeCards, setChildrenStyle } from 'utilities'

// Bump this whenever QUERY_BODY_SWATCHES/QUERY_BODY_SWATCH_GROUPS changes shape,
// so stale cached payloads from before the change are ignored instead of reused.
const SWATCHES_CACHE_VERSION = 'v2'
const SWATCHES_STORAGE_KEY = `swatches:${SWATCHES_CACHE_VERSION}`
const SWATCH_GROUPS_STORAGE_KEY = `swatchGroups:${SWATCHES_CACHE_VERSION}`
// Cached product data includes market-specific pricing (queried with
// @inContext(country: Shopify.country) below), so it's scoped per market —
// otherwise switching markets keeps serving another market's cached prices
// out of localStorage until that entry happens to get overwritten.
const LOADED_PRODUCTS_STORAGE_KEY = `loadedProducts:${SWATCHES_CACHE_VERSION}:${Shopify.country}`

const QUERY_BODY_PRODUCTS = `
    nodes {
      id
      title
      description
      handle
      availableForSale
      tags
      totalInventory
      onlineStoreUrl
      images(first:250) {
        nodes {
          id
          url
          altText
          height
          width
        }
      }
      shippingDate: metafield(key: "preorder_estimated_shipping_date", namespace: "custom") {
        value
      }
      compareAtPriceRange {
        maxVariantPrice {
          amount
        }
        minVariantPrice {
          amount
        }
      }
      priceRange {
        maxVariantPrice {
          amount
        }
        minVariantPrice {
          amount
        }
      }
      variants(first: 10) {
        nodes {
          id
          availableForSale
          title
          quantityAvailable
          price {
            amount
            currencyCode
          }
          selectedOptions {
            name
            value
          }
          shippingDate: metafield(key: "preorder_estimated_shipping_date", namespace: "custom") {
            value
          }
        }
      }
    }
`

const QUERY_BODY_SWATCHES = `
    nodes {
      id
      handle
      fields {
        key
        value
        reference {
          ...on MediaImage {
            image {
              url
            }
          }
          ...on Metaobject {
            id
            handle
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
`

const QUERY_BODY_SWATCH_GROUPS = `
    nodes {
      id
      handle
      fields {
        key
        value
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
`


/**
 * Flattens a single-layer object's properties and values into a string, separated by a specified delimiter.
 *
 * @param {Object} params - The object to be flattened.
 * @param {string} [separator=':'] - The separator to be used between the key and value.
 * @param {string} [delimiter=', '] - The delimiter to be used between the key-value pairs.
 * @returns {string} - A string representation of the flattened object.
 * @example
 * const params = {
 *   color: 'red',
 *   size: 'medium'
 * }
 * const flattenedParams = flattenParams(params)
 * console.log(flattenedParams) // Output: "color:red,size:medium"
 */
export const flattenParams = (params, separator = ':', delimiter = ', ') => Object.entries(params).flatMap(([key, value]) => value ? [`${key}${separator}${typeof value === 'string' ? `"${value}"` : value}`] : []).join(delimiter)


/**
 * Constructs a GraphQL query string using the provided type, parameters, and body.
 *
 * @param {Object} [options] - Query options.
 * @param {string} [options.type] - The type of GraphQL query to construct.
 * @param {Object} [options.params] - The parameters to be included in the query.
 * @param {string} [options.body] - The body of the GraphQL query.
 * @returns {string} - A string representation of the constructed GraphQL query.
 */
export const constructGQLQuery = ({ type, body, params } = {}) => `
query @inContext(country: ${Shopify.country}) {
  ${type}${params ? `(${flattenParams(params)})` : ''} {
    ${body.trim()}
  }
}
`.trim()


/**
 * Fetches data from the Shopify GraphQL API using the provided type, parameters, and body.
 *
 * @param {Object} [data] - Query data.
 * @param {string} [data.type] - The type of GraphQL query to construct.
 * @param {Object} [data.params] - The parameters to be included in the query.
 * @param {string} [data.body] - The body of the GraphQL query.
 * @param {Object} [options] - Function options.
 * @param {number} [options.maxAttempts=5] - The maximum number of attempts used when query fails.
 * @param {number} [options.retryTimeout=200] - The amount of time to wait before retrying a failed query.
 * @returns {Promise<object>} - A promise that resolves to the data returned by the GraphQL API.
 */
export const queryStorefront = async ({ type, params, body } = {}, { maxAttempts = 5, retryTimeout = 100 } = {}) => {
  let attempts = 0
  const query = constructGQLQuery({ type, params, body })
  const runQuery = async () => {
    const response = await fetch(
      `${window.Shopify.routes.root}api/2024-04/graphql.json`,
      {
        method: 'POST',
        headers: {
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN_VPC,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query })
      }
    )

    const { data, errors } = await response.json()

    if (!data) {
      attempts++
      const failString = `Failed to perform GraphQL query ${type ? `for ${type}` : null}`
      if (attempts < maxAttempts) {
        await sleep(retryTimeout)
        console.debug(`${failString}. Retrying...`, `(attempt: ${attempts + 1} of ${maxAttempts})`)
        return await runQuery()
      }

      console.error(failString, `after ${attempts} attempts`)
      console.error(...(errors ?? []).map(error => `GraphQL Error: ${error.message}`))
      console.debug('Failed Storefront API Query:\n', query)

      return false
    }

    return data
  }

  const data = await runQuery()
  if (!data) return false

  if (Object.keys(data[type]).length === 1 && 'nodes' in data[type]) {
    return parseNodes(data[type].nodes)
  }

  return data[type] ?? data
}


/**
 * Parses a node from data returned by a GraphQL query, simplifying the node's data structure by directly appending property nodes to their parent.
 *
 * @param {Object} node - The node object to be parsed.
 * @returns {Object} - A simplified representation of the input node.
 * @example
 * const node = {
 *   name: 'ocean',
 *   fish: {
 *     nodes: [
 *       { name: 'tuna', size: 'big' },
 *       { name:'salmon', size:'small' },
 *     ]
 *   }
 * }
 * const flattenedObject = parseNode(node)
 * console.log(flattenedObject) // Output: { name: 'ocean', fish: [{ name: 'tuna', size: 'big' }, { name: 'salmon', size: 'small' }] }
 */
export const parseNode = node => Object.entries(node).reduce((acc, [key, value]) => {
  const skipKeys = ['nodes', 'value']
  if (value && typeof value === 'object') {
    if (Array.isArray(value)) {
      return { ...acc, [key]: parseNodes(value) }
    }

    const valueKeys = Object.keys(value)
    // Remove unnecessary intermediary properties
    if (valueKeys.length === 1 && skipKeys.includes(valueKeys[0])) {
      value = value[valueKeys[0]] ?? value
    }

    if (typeof value === 'object') {
      value = Array.isArray(value) ? parseNodes(value) : parseNode(value)
    }
  }

  return { ...acc, [key]: value }
}, {})


/**
 * Parses nodes returned by a GraphQL query, simplifying each node's data structure by directly appending property nodes to their parent.
 * Applies the parseNode function to each node in the array.
 *
 * @param {Array} nodes - The array of nested objects to be parsed.
 * @returns {Array} - A simplified representation of the input array.
 */
export const parseNodes = nodes => nodes.map(parseNode)


/**
 * Parses a metaobject's fields, simplifying each field's data 
 * structure by directly appending property values to their parent.
 *
 * @param {Object} metaobject - The metaobject to be parsed.
 * @returns {Object} - A simplified representation of the input metaobject.
 * @example
 * const oceanMetaobject = {
 *   fields: [
 *     { key: 'size', value: 'big' },
 *     { key: 'hasOctopi', value: true },
 *     { key: 'hasElephants', value: false },
 *     { key: 'image', value: { reference: { image: { url: { '/path/to/ocean.png' } } } } },
 *   ]
 * }
 * const parsedMetaobject = parseMetaobject(metaobject)
 * console.log(parsedMetaobject) // Output: { size: 'big', hasOctopi: true, hasElephants: false, image: '/path/to/ocean.png' }
 */
export const parseMetaobject = metaobject => Object.entries(metaobject).reduce((acc, [key, value]) => {
  if (key === 'fields') {
    for (const field of value) {
      if (field.key === 'image') {
        acc[field.key] = field.reference.image.url
      } else if (field.reference?.id) {
        // Generic metaobject reference (e.g. a swatch's group)
        acc[field.key] = { id: field.reference.id, handle: field.reference.handle }
      } else {
        const fieldValue = typeof field.value === 'string' ? field.value.trim() : field.value
        acc[field.key] = fieldValue
      }
    }
  } else {
    acc[key] = value
  }

  return acc
}, {})


/**
 * Fetches all swatch metaobjects and returns them as an object with swatch names as keys.
 *
 * @returns {Object} An object with swatch names as keys and swatch objects as values.
 */
export const getSwatches = async () => {
  if (window.swatches) return window.swatches
  // Coalesce concurrent callers onto the same in-flight fetch
  if (window.swatchesPromise) return window.swatchesPromise

  const storedSwatches = JSON.parse(localStorage.getItem(SWATCHES_STORAGE_KEY) || 'null')
  if (storedSwatches) {
    // Trigger swatch fetch in background to ensure data is up-to-date
    window.requestIdleCallback(fetchSwatches)
    return storedSwatches
  }

  window.swatchesPromise = fetchSwatches()
  return window.swatchesPromise
}


/**
 * Fetches swatch metaobjects from the Shopify Storefront API.
 *
 * @returns {Object} An object with swatch names as keys and swatch objects as values.
 */
export const fetchSwatches = async () => {
  let swatches = []
  let pageInfo = {
    hasNextPage: true,
    endCursor: null
  }
  const type = 'metaobjects'
  const body = QUERY_BODY_SWATCHES

  let attempts = 0;
  const getPageResults = async pageInfo => {
    const MAX_ATTEMPTS = 10
    const params = {
      type: 'swatch',
      first: 250,
      after: pageInfo.endCursor
    }
    const response = await queryStorefront({ type, params, body })

    if (!response.nodes) {
      attempts++

      if (attempts >= MAX_ATTEMPTS) {
        console.error('Failed to fetch swatches after multiple attempts', response)
        return false
      }

      await sleep(100)
      console.debug('Failed to fetch swatches. Retrying...', `(attempt: ${attempts + 1} of ${MAX_ATTEMPTS})`)
      return await getPageResults(pageInfo)
    }

    attempts = 0

    return response
  }

  // Continue fetching swatches until there are none left
  while (pageInfo.hasNextPage) {
    const response = await getPageResults(pageInfo)

    if (response.nodes) {
      pageInfo = response.pageInfo
      swatches = [...swatches, ...response.nodes]
    }
  }

  // Flatten metaobject fields and reduce to object with swatch names as keys
  window.swatches = prepareSwatches(swatches.map(parseMetaobject))
  localStorage.setItem(SWATCHES_STORAGE_KEY, JSON.stringify(window.swatches))

  return window.swatches
}

export const prepareSwatches = swatches => Array.isArray(swatches) ? swatches.reduce((acc, swatch) => ({ ...acc, [swatch.name.trim()]: swatch }), {}) : swatches


/**
 * Fetches all swatch_group metaobjects and returns them keyed by metaobject id,
 * with a numeric sort_order for stable, admin-editable ordering.
 *
 * @returns {Object} An object keyed by group id: { [id]: { name, sortOrder } }
 */
export const getSwatchGroups = async () => {
  if (window.swatchGroups) return window.swatchGroups
  // Coalesce concurrent callers onto the same in-flight fetch
  if (window.swatchGroupsPromise) return window.swatchGroupsPromise

  window.swatchGroupsPromise = fetchSwatchGroups()
  return window.swatchGroupsPromise
}

export const fetchSwatchGroups = async () => {
  let groups = []
  let pageInfo = { hasNextPage: true, endCursor: null }
  const type = 'metaobjects'
  const body = QUERY_BODY_SWATCH_GROUPS

  let attempts = 0
  const getPageResults = async pageInfo => {
    const MAX_ATTEMPTS = 10
    const params = { type: 'swatch_group', first: 250, after: pageInfo.endCursor }
    const response = await queryStorefront({ type, params, body })

    if (!response.nodes) {
      attempts++
      if (attempts >= MAX_ATTEMPTS) {
        console.error('Failed to fetch swatch groups after multiple attempts', response)
        return false
      }
      await sleep(100)
      return await getPageResults(pageInfo)
    }

    attempts = 0
    return response
  }

  while (pageInfo.hasNextPage) {
    const response = await getPageResults(pageInfo)
    if (response.nodes) {
      pageInfo = response.pageInfo
      groups = [...groups, ...response.nodes]
    }
  }

  window.swatchGroups = groups.map(parseMetaobject).reduce((acc, group) => ({
    ...acc,
    [group.id]: { name: group.name, sortOrder: Number(group.sort_order ?? 0) },
  }), {})

  localStorage.setItem(SWATCH_GROUPS_STORAGE_KEY, JSON.stringify(window.swatchGroups))

  return window.swatchGroups
}


/**
 * Filters products based on whether they are on sale or not.
 *
 * @param {boolean|null} onSale - A boolean value indicating whether the product should be on sale.
 * @param {Object} product - The product object.
 * @returns {boolean} A boolean value indicating whether the product matches sale criteria.
 */
export const filterBySale = onSale => product => onSale === null || product.onSale === onSale


/**
 * Returns all matching variant products that match product's sale status.
 *
 * @param {string} productTitle - The base title of the product to search for.
 * @param {Object} [options] - Query options.
 * @param {boolean} [options.queryBySale=false] - Whether returned products should be on sale.
 * @returns {Array} An array of products that match the provided productTitle.
 */
window.loadedProducts ??= {}
export const getSwatchProducts = async (productTitle, { queryBySale = false } = {}) => {
  if (!productTitle) return

  const saleFilter = filterBySale(queryBySale)

  window.storedProducts ??= JSON.parse(localStorage.getItem(LOADED_PRODUCTS_STORAGE_KEY) || '{}')
  if (storedProducts[productTitle]) {
    fetchSwatchProduct(productTitle)
    if (document.body.dataset.b2b == 'true') {
      return storedProducts[productTitle]
    } else {
      return storedProducts[productTitle].filter(saleFilter)
    }
  }

  if (productTitle in window.loadedProducts) {
    // Wait for promise to resolve if existing query for product is in progress
    const products = await window.loadedProducts[productTitle]
    // Return products that match sale criteria if available
    if (products?.length) {
      if (document.body.dataset.b2b == 'true') {
        return products
      } else {
        return products.filter(saleFilter)
      }
    }
  }

  fetchSwatchProduct(productTitle)

  // Return products that match sale criteria once promise resolves
  if (document.body.dataset.b2b == 'true') {
    return (await window.loadedProducts[productTitle])
  } else {
    return (await window.loadedProducts[productTitle]).filter(saleFilter)
  }
}

const fetchSwatchProduct = async productTitle => {
  // Immediately assign swatch query promise to global variable to prevent duplicate queries
  window.loadedProducts[productTitle] = querySwatchProducts(productTitle)
  debouncedStoreSwatchProducts()
}

const storeSwatchProducts = async (storedProducts) => {
  await Promise.all(Object.entries(window.loadedProducts).map(async ([productTitle, queryPromise]) => {
    const products = await queryPromise
    window.loadedProducts[productTitle] = products
    return products
  }))

  localStorage.setItem(LOADED_PRODUCTS_STORAGE_KEY, JSON.stringify({ ...storedProducts, ...window.loadedProducts }))
}

const debouncedStoreSwatchProducts = debounce(storeSwatchProducts, 300)

const escapeQuery = str => str.replace(/([:'\(\)\\])/g, '\\\\$1').replace(/(")/g, '\\$1')

export const getProductTitleParts = title => {
  const parts = title.split(' - ').map(part => part.trim())
  if (parts.length < 2) {
    console.warn('Invalid product title format, no color supplied:', title)
    return [title]
  }

  const color = parts.pop()
  title = parts.join(' - ')

  return [title, color]
}

/**
 * Fetches all variant products, returning products that have a title matching the pattern of `${productTitle} - ${color}`.
 *
 * @param {string} productTitle - The base title of the product to search for.
 * @returns {Array} An array of products that match the provided productTitle.
 */
const querySwatchProducts = async productTitle => {
  productTitle = productTitle.trim()
  const swatches = await getSwatches()

  const type = 'products'
  const params = {
    query: `title:'${escapeQuery(productTitle)} - '*`,
    first: 40,
  }
  const body = QUERY_BODY_PRODUCTS
  const data = await queryStorefront({ type, params, body })

  if (!data.length) return

  // Use flatmap to filter and map simultaneously
  const products = data.flatMap(product => {
    // Filter out any products unavailable to online store sales channel
    if (!product.onlineStoreUrl) {
      product.onlineStoreUrl = `https://${window.Shopify.shop}/products/${product.handle}`

      // return []
    }

    // Filter out any non-variant products and add swatch data
    if (product.title.toLowerCase() === productTitle.toLowerCase()) return []

    const [title, color] = getProductTitleParts(product.title)
    // Filter out any extra fuzzy-match products
    if (title.toLowerCase() !== productTitle.toLowerCase()) return []

    // Check product sale status
    const { priceRange, compareAtPriceRange: compareAt } = product
    const onSale = (
      parseFloat(priceRange.minVariantPrice.amount) < parseFloat(compareAt.minVariantPrice.amount) ||
      parseFloat(priceRange.maxVariantPrice.amount) < parseFloat(compareAt.maxVariantPrice.amount)
    )

    const swatch = swatches[color]

    // Return product with swatch and sale data
    return [{
      ...product,
      onSale,
      swatch
    }]
  })

  return products
}


const swatchClasses = ['color-swatch', window.themeVariables.settings.roundSwatches ? 'rounded-full' : null].filter(Boolean)

const SWATCH_FLAG_NEW_TAG = '_swatchtag:new'
const SWATCH_FLAG_LABELS = {
  sale: window.themeVariables.strings.swatchFlagSale || 'Sale',
  new: window.themeVariables.strings.swatchFlagNew || 'New',
}

/**
 * Returns the flag to display on a product's swatch. A swatch shows at most
 * one flag, and Sale takes priority over New.
 *
 * @param {Object} product - The swatch product (see querySwatchProducts()).
 * @param {Object} [options] - Flag options.
 * @param {boolean} [options.sale=true] - Whether the Sale flag may be shown.
 * @returns {string|null} 'sale', 'new' or null.
 */
export const getSwatchFlag = (product, { sale = true } = {}) => {
  if (sale && product.onSale) return 'sale'
  // parseNode() turns each string tag into a character map ({ 0: '_', 1: 's', ... }),
  // so rebuild the string the same way ProductCard.productTags does.
  const tags = Array.isArray(product.tags) ? product.tags.map(tag => Object.values(tag).join('')) : []
  if (tags.some(tag => tag.toLowerCase() === SWATCH_FLAG_NEW_TAG)) return 'new'
  return null
}

/**
 * Returns swatch element markup based on the provided product and options.
 *
 * @param {Object} [options] - Swatch options.
 * @param {Object} [options.product] - The product object containing swatch information.
 * @param {string} [options.idPrefix=''] - A custom ID prefix for the swatch input element.
 * @param {string} [options.name='color'] - The name of the swatch input element.
 * @param {boolean} [options.active=false] - Whether the swatch is currently selected.
 * @param {boolean} [options.tooltip=false] - Whether to show a value tooltip on hover.
 * @param {Array} [options.classes=['color-swatch', 'rounded-full']] - An array of classes to apply to the swatch label.
 * @param {string|null} [options.flag=null] - Swatch flag to display ('sale' or 'new'), see getSwatchFlag().
 * @returns {string} A string representing the HTML markup for the swatch element.
 */
export const buildSwatch = ({
  product,
  idPrefix,
  name = 'color',
  active = false,
  size = 'md',
  classes = [],
  tooltip = false,
  flag = null,
} = {}) => {
  const colorName = product.title.split(' - ').pop().trim()
  const productName = product.title.split(' - ').shift().trim()
  const publicColorName = colorName.replace(productName, "")
  const { swatch: { color, image } = {} } = product
  const id = (idPrefix ? `${idPrefix}-` : '') + handle(colorName)
  classes = [...new Set([...swatchClasses, ...classes])]
  tooltip = tooltip ? ` data-tooltip="${colorName}"` : ''
  // TODO: Consider using swatch color contrast calculation here instead!
  // if (colorName.toLowerCase() === 'white') classes.push('border')

  const disabledClassIndex = classes.indexOf('is-disabled')
  if (disabledClassIndex !== -1 && product.availableForSale) {
    classes.splice(disabledClassIndex, 1)
  } else if (disabledClassIndex === -1 && !product.availableForSale) {
    classes.push('is-disabled')
  }

  const background = image ? `url(${image}&width=95)` : `linear-gradient(to right, ${color}, ${color})`
  const checked = active ? 'checked' : ''
  const flagLabel = flag ? SWATCH_FLAG_LABELS[flag] : null

  return `
    <span class="product-swatch swatch-wrapper">
      <input class="sr-only" type="radio" name="${name}" id="${id}" value="${colorName}" ${checked}>
      <label class="${classes.join(' ')}" for="${id}"${tooltip} data-option-value style="--swatch-background: ${background}; --swatch-background-fallback: ${color};" data-option-size="${size}">
        <span class="product-swatch__label-text">${publicColorName}</span>
        <span class="sr-only"> - </span>
        ${flagLabel ? `<span class="swatch-flag swatch-flag--${flag}">${flagLabel}</span>` : ''}
      </label>
    </span>
  `
}


/**
 * Renders swatch elements based on the provided product and options.
 *
 * @param {Object} [options] - Swatch options.
 * @param {Object} [options.container] - The container element where the swatch elements will be rendered.
 * @param {Array} [options.products] - An array of product objects containing swatch information.
 * @param {string} [options.idPrefix=''] - A custom ID prefix for the swatch input elements.
 * @param {string} [options.name='color'] - The name of the swatch input elements.
 * @param {string} [options.current] - The currently selected swatch color.
 * @param {boolean} [options.grouped=true] - Whether to bucket swatches by their metaobject group (product page). Pass false to render them as a single flat list (product cards).
 * @param {Object|false} [options.flags=false] - Sale/New flag options passed to getSwatchFlag() (product page), or false to show no flags (product cards).
 * @returns {void} Renders the swatch elements within the specified container.
 */
export const renderSwatches = ({
  container,
  products,
  idPrefix,
  current,
  name = 'color',
  grouped = true,
  flags = false,
} = {}) => {
  const baseSwatch = container.querySelector('.color-swatch')
  const size = baseSwatch.dataset.optionSize
  const classes = [...baseSwatch?.classList]
  const tooltip = !!baseSwatch.dataset.tooltip
  const gapClass = container.dataset.gap ? `gap-${container.dataset.gap}` : 'gap-2.5'

  const groups = window.swatchGroups || {}

  const visibleProducts = products.filter(product => {
    const active = product.swatch?.name === current
    return active || product.availableForSale
  })

  // Only group if every visible swatch has a group assigned and there's
  // more than one distinct group among them — a single ungrouped swatch,
  // or a product whose swatches all share one group, falls back to the
  // regular flat list with no group markup at all.
  const visibleGroupIds = new Set(visibleProducts.map(product => product.swatch?.group?.id))
  const effectiveGrouped = grouped && !visibleGroupIds.has(undefined) && visibleGroupIds.size > 1

  let swatchesMarkup = ''

  if (!effectiveGrouped) {
    for (const product of visibleProducts) {
      swatchesMarkup += buildSwatch({
        product,
        idPrefix,
        name,
        active: product.swatch?.name === current,
        size,
        classes,
        tooltip,
        flag: flags ? getSwatchFlag(product, flags) : null,
      })
    }
  } else {
    const buckets = new Map()
    for (const product of visibleProducts) {
      const groupId = product.swatch.group.id
      if (!buckets.has(groupId)) buckets.set(groupId, [])
      buckets.get(groupId).push(product)
    }

    const sortedGroupIds = [...buckets.keys()].sort((a, b) => (groups[a]?.sortOrder ?? 0) - (groups[b]?.sortOrder ?? 0))

    for (const groupId of sortedGroupIds) {
      let itemsMarkup = ''

      for (const product of buckets.get(groupId)) {
        itemsMarkup += buildSwatch({
          product,
          idPrefix,
          name,
          active: product.swatch?.name === current,
          size,
          classes,
          tooltip,
          flag: flags ? getSwatchFlag(product, flags) : null,
        })
      }

      const groupName = groups[groupId]?.name
      const headingId = `swatch-group-${Math.random() * 10}-heading`

      swatchesMarkup += `
      <div class="swatch-group" role="group" aria-labelledby="${headingId}">
        ${groupName ? `
          <span class="swatch-group__heading h6" id="${headingId}">
            ${groupName}
          </span>
        ` : ''}
        <span class="swatch-group__items h-stack wrap ${gapClass}">
          ${itemsMarkup}
        </span>
      </div>
    `
    }
  }

  container.innerHTML = swatchesMarkup
  container.removeAttribute('aria-hidden')
}





/******************
 * WEB COMPONENTS *
 ******************/

/**
 * A variant picker component extended for use with ProductSwatches.
 *
 * @extends VariantPicker
 */
export class ProductVariantPicker extends VariantPicker {
  connectedCallback() {
    super.connectedCallback()
    this.initialProductId =
      document.forms[this.getAttribute('form')]['product-id'].value
    this.productsCache = {}

    this.initProduct()
  }

  async initProduct() {
    await this.loadProduct()
    this.masterSelector.dispatchEvent(new Event('change', { bubbles: true }))

    if (this.isPDP) {
      this.productsCache[this.product.id] = document.documentElement.outerHTML
    }
  }

  async loadProduct() {
    this.product = await ProductLoader.load(this.productHandle)
    window.soldOutVariants = this.product.variants.flatMap(variant => !variant.available ? [String(variant.id)] : [])
  }

  get selectedVariant() {
    return (
      this._getVariantById(parseInt(this.masterSelector.value)) ||
      this._getPreviousVariantById(parseInt(this.masterSelector.value))
    )
  }

  get sectionId() {
    return this.dataset.originalSection
      ? this.dataset.originalSection
      : this.dataset.section
  }

  get section() {
    return document.getElementById(`shopify-section-${this.sectionId}`)
  }

  get root() {
    return this.isQuickBuy ? this.closest('.quick-buy-modal__content') : this.section
  }

  get mediaGallery() {
    return document.querySelector(
      `product-gallery[form="${this.getAttribute('form')}"]`,
    )
  }

  get extraInformation() {
    return this.closest('.product').querySelector('#product-extra-information')
  }

  get relatedProducts() {
    return this.closest('main')?.querySelector('.shopify-section--related-products')
  }

  get context() {
    return this.getAttribute('context') ?? 'main'
  }

  get isPDP() {
    return this.context === 'main'
  }

  get isQuickBuy() {
    return this.context === 'quick-buy'
  }

  get isFeatured() {
    return this.context === 'featured'
  }

  set isUpdating(value) {
    this.updating = value

    if (value) {
      window.lockHeader = true
      this.triggerUpdateEvent()
    } else {
      window.lockHeader = false
      this.triggerUpdateEvent(false)
    }
  }

  get isUpdating() {
    return this.updating
  }

  _getPreviousVariantById(id) {
    return this.previousProduct?.['variants'].find(
      variant => variant['id'] === id,
    )
  }

  _getVariantById(id) {
    return this.product['variants'].find(variant => variant['id'] === id)
  }

  _getVariantFromOptions() {
    const options = this._getSelectedOptionValues()
    return this.product['variants'].find(variant => {
      return variant['options'].every(
        (value, index) => value === options[index],
      )
    })
  }

  _isVariantSelectable(variant) {
    if (['string', 'number'].includes(typeof variant))
      variant = this._getVariantById(variant)

    if (!variant || !this.product.variants.find(v => v.id === variant.id)) {
      return false
    } else {
      return (
        variant['available'] ||
        (!this.hideSoldOutVariants && !variant['available'])
      )
    }
  }

  _getSelectedOptionValues() {
    return this.optionSelectors.map(optionSelector => {
      return optionSelector.querySelector(
        'input[name^="option"][type="hidden"], input[name^="option"]:checked, select[name^="option"]',
      ).value
    })
  }

  _validateVariantId(id) {
    if (!id || !this._isVariantSelectable(id)) {
      const variant =
        this._getVariantFromOptions() ??
        this._getFirstMatchingAvailableOrSelectableVariant()
      id = variant.id
    }

    return id
  }

  _getFirstMatchingAvailableOrSelectableVariant() {
    let options = this._getSelectedOptionValues()
    let matchedVariant = null
    let slicedCount = 0

    do {
      options.pop()
      slicedCount += 1
      matchedVariant = this.product['variants'].find(variant => {
        if (this.hideSoldOutVariants) {
          return (
            variant['available'] &&
            variant['options']
              .slice(0, variant['options'].length - slicedCount)
              .every((value, index) => value === options[index])
          )
        } else {
          return variant['options']
            .slice(0, variant['options'].length - slicedCount)
            .every((value, index) => value === options[index])
        }
      })
    } while (!matchedVariant && options.length > 0)

    return matchedVariant
  }

  async prepareGalleryReplacement(newMediaGallery) {
    const productContainer = this.mediaGallery.closest('.product, [data-modal-content-wrapper]')
    let imageParentSelectors = ['.product-gallery__carousel .is-initial']
    const thumbnails = this.mediaGallery.querySelector('product-gallery-navigation')
    if (thumbnails) {
      const thumbnailParentStyles = getComputedStyle(thumbnails.parentElement)
      if (thumbnailParentStyles.getPropertyValue('display') !== 'none') {
        imageParentSelectors.push('product-gallery-navigation')
      }
    }
    imageParentSelectors = imageParentSelectors.join(', ')

    await imagesLoaded(newMediaGallery.querySelectorAll(imageParentSelectors), { container: true })

    productContainer.style.position = 'relative'
    const transientGallery = newMediaGallery.cloneNode(true)
    Object.assign(transientGallery.style, {
      // TODO: Handle dot-pagination replacement in a better way (bg prevents overlaps)
      position: 'absolute',
      top: `${this.mediaGallery.offsetTop}px`,
      width: `${this.mediaGallery.offsetWidth}px`,
      height: `${this.mediaGallery.offsetHeight}px`,
      opacity: 0,
    })
    productContainer.append(transientGallery)
    await transientGallery.animate(
      { opacity: [0, 1] },
      { duration: 150, easing: 'ease-in', fill: 'forwards' },
    ).finished

    return async () => {
      this.mediaGallery.replaceWith(newMediaGallery)
      await imagesLoaded(imageParentSelectors, { container: true, root: this.mediaGallery })
      transientGallery.remove()
    }
  }

  getProductMarkup(id) {
    const markup = this.productsCache[id]
    const incomingSectionId = markup.match(/data-section="([^"]*)"/)[1]

    const html = new DOMParser().parseFromString(
      markup.replaceAll(this.product.id, this.initialProductId).replaceAll(incomingSectionId, this.sectionId),
      'text/html',
    )

    if (this.isQuickBuy) {
      const quickBuyContent = html.querySelector('#quick-buy-content').content
      Array.from(quickBuyContent.querySelectorAll('noscript')).forEach(noScript => noScript.remove())

      html.body.replaceChildren(quickBuyContent)
    }

    return html
  }

  async getVariantProduct(product, parse = true) {
    const id = parseGID(product.id)
    const productUrl = product.url ?? product.onlineStoreUrl

    if (!this.productsCache[id]) {
      const url = new URL(productUrl, window.location)
      const response = await fetch(`${url.pathname}${url.search}${url.hash}`)
      const responseText = await response.text()
      this.productsCache[id] = responseText
    }

    return parse ? this.getProductMarkup(id) : this.productsCache[id]
  }

  // updateRelatedProducts(html) {
  //   const relatedProducts = this.relatedProducts
  //   const newRelatedProducts = html.querySelector('.shopify-section--related-products')

  //   if (!newRelatedProducts) {
  //     relatedProducts?.remove()
  //     return
  //   }

  //   if (!relatedProducts) {
  //     this.section.insertAdjacentElement('afterend', newRelatedProducts)
  //     return
  //   }

  //   const relatedProductCards = relatedProducts.querySelectorAll('.product-card')
  //   const newRelatedProductCards = newRelatedProducts.querySelectorAll('.product-card')

  //   // Replace the section if related product count does not match
  //   // TODO: Handle addition / removal of cards if budget becomes available
  //   if (relatedProductCards.length !== newRelatedProductCards.length) {
  //     relatedProducts.replaceWith(newRelatedProducts)
  //     return
  //   }

  //   for (let i = 0; i < relatedProductCards.length; i++) {
  //     const relatedProductCard = relatedProductCards[i]
  //     const newRelatedProductCard = newRelatedProductCards[i]

  //     if (relatedProductCard.getAttribute('handle') !== newRelatedProductCard.getAttribute('handle')) {
  //       fadeReplace(relatedProductCard, newRelatedProductCard)
  //     }
  //   }
  // }

  async updateElements(variantId, html) {
    const requestedProductId = this.product.id

    html ??= await this.getVariantProduct(this.product)

    if (requestedProductId !== this.product.id) return

    const mediaGallery = html.querySelector('product-gallery')

    const replaceGallery = await this.prepareGalleryReplacement(mediaGallery)

    const blockSelector = '[data-variant-block]'
    const blocks = [...html.querySelectorAll(blockSelector)]

    // Blocks only render when they have content (e.g. the Final Sale modal
    // only exists on products tagged final-sale), so the incoming product can
    // be missing blocks the current one has, or have blocks it lacks.
    const incomingBlockIds = new Set(blocks.map(block => block.dataset.variantBlock))
    this.root.querySelectorAll(blockSelector).forEach(block => {
      if (!incomingBlockIds.has(block.dataset.variantBlock)) this.removeBlock(block)
    })

    blocks.forEach(this.replaceBlock.bind(this))

    const templateSuffix = html.querySelector('buy-buttons').getAttribute('template')
    this.root.querySelector('buy-buttons').setAttribute('template', templateSuffix)

    const preorderInfoCurrent = this.root.querySelector('.buy-buttons__preorder-date')
    const preorderListsCurrent = this.root.querySelector('.preorder-variant-lists')
    const preorderInfoFetched = html.querySelector('.buy-buttons__preorder-date')
    const preorderListsFetched = html.querySelector('.preorder-variant-lists')
    const buyButtons = this.root.querySelector('buy-buttons')

    if (preorderInfoCurrent && preorderListsCurrent && preorderInfoFetched && preorderListsFetched) {
      preorderInfoCurrent.replaceWith(preorderInfoFetched)
      preorderListsCurrent.replaceWith(preorderListsFetched)
    } else if (preorderInfoFetched && preorderListsFetched) {
      buyButtons.appendChild(preorderInfoFetched)
      buyButtons.appendChild(preorderListsFetched)
    } else {
      preorderInfoCurrent?.remove()
      preorderListsCurrent?.remove()
    }

    // this.root.querySelectorAll('[data-option="size"] .preorder-icon').forEach(icon => icon?.remove())

    this.updateProductId()

    // if (this.isPDP) {
    //   this.updateRelatedProducts(html)
    // }

    super.selectVariant(variantId)

    await replaceGallery()
  }

  async replaceBlock(block, index, blocks) {
    const targetBlock = this.root.querySelector(`[data-variant-block="${block.dataset.variantBlock}"]`)

    if (!targetBlock) return this.insertBlock(block, index, blocks)

    // Maintain open state when it exists
    if ('toggle' in targetBlock && targetBlock.hasAttribute('open')) {
      block.toggle(true, false)
    }

    await fadeCards(targetBlock, false)
    setChildrenStyle(block, '.product-card', { opacity: 0 })
    targetBlock.replaceWith(block)
    await fadeCards(block)

    setChildrenStyle(block, '.product-card', { opacity: 1 })
  }

  /**
   * Inserts an incoming block that has no counterpart on the page, next to the
   * nearest incoming block (before it, else after it) that does.
   *
   * @param {HTMLElement} block - The incoming block to insert.
   * @param {number} index - The block's index in the incoming blocks list.
   * @param {HTMLElement[]} blocks - All incoming blocks, in document order.
   */
  insertBlock(block, index, blocks) {
    const groupSelector = '.product-info__block-group'
    const findCurrent = incoming => this.root.querySelector(`[data-variant-block="${incoming.dataset.variantBlock}"]`)

    // Position relative to an anchor: inside its block group when the incoming
    // block shares that group, otherwise next to the group as a whole.
    const getPlacement = incomingAnchor => {
      const currentAnchor = findCurrent(incomingAnchor)
      if (!currentAnchor) return null

      const sameGroup = block.closest(groupSelector) === incomingAnchor.closest(groupSelector)
      return sameGroup ? currentAnchor : (currentAnchor.closest(groupSelector) ?? currentAnchor)
    }

    for (let i = index - 1; i >= 0; i--) {
      const anchor = getPlacement(blocks[i])
      if (anchor) return anchor.after(block)
    }

    for (let i = index + 1; i < blocks.length; i++) {
      const anchor = getPlacement(blocks[i])
      if (anchor) return anchor.before(block)
    }
  }

  async removeBlock(block) {
    await fadeCards(block, false)
    const group = block.closest('.product-info__block-group')
    block.remove()
    if (group && !group.querySelector('.product-info__block-item')) group.remove()
  }

  updateProductId() {
    if (!this.previousProduct) return

    const previousProductId = this.previousProduct.id
    const previousProductUrl = this.previousProduct.url ?? this.previousProduct.onlineStoreUrl
    const productUrl = this.product.url ?? this.product.onlineStoreUrl

    document
      .querySelectorAll(`[value="${previousProductId}"]`)
      .forEach(input => (input.value = this.product.id))

    this.root
      .querySelectorAll(`[href="${previousProductUrl}"]`)
      .forEach(el => el.setAttribute('href', productUrl))

    this.root.querySelectorAll(`[data-id="${previousProductId}"]`).forEach(el => el.dataset.id = this.product.id)
    this.root.querySelectorAll(`[data-product-id]`).forEach(el => el.dataset.productId = this.product.id)
  }

  triggerUpdateEvent(active = true) {
    const eventType = active ? 'start' : 'end'
    document.documentElement.dispatchEvent(new CustomEvent(`theme:loading:${eventType}`, { bubbles: true }))
    this.dispatchEvent(new CustomEvent(`product:update:${eventType}`, {
      detail: {
        product: this.product,
      },
      bubbles: true
    }));
  }

  async updateVariantProduct(variantId) {
    // TODO: Consider implement retry system if this is ever hit
    if (this.isUpdating) return

    this.isUpdating = true

    this.previousProduct = this.product
    await this.loadProduct()

    // Ensure variant ID is based on new product
    variantId = this._validateVariantId(variantId)

    // TODO: Move all this to other method
    if (window.ShopifyAnalytics && ShopifyAnalytics.meta.product) {
      const { id, type, vendor } = this.product
      const gid = `gid://shopify/Product/${id}`
      const variants = this.product.variants.map(({ id, name, price, public_title, sku }) => ({ id, name, price, public_title, sku }))
      ShopifyAnalytics.meta.product = { id, gid, type, variants, vendor }
    }

    const incomingProductMarkup = await this.getVariantProduct(this.product)

    const emVariantDataEl = document.querySelector('#em_product_variants')
    if (emVariantDataEl) {
      const variantDataEl = incomingProductMarkup.querySelector('#product_variant_data')

      if (variantDataEl) {
        const variantData = variantDataEl.textContent
        window.variantData = variantData
        emVariantDataEl.textContent = variantData
      }
    }

    if (this.updateUrl && !this.isQuickBuy) {
      const url = new URL(this.product.url, window.location)
      url.searchParams.set('variant', variantId)
      window.history.replaceState({ path: url.toString() }, '', url.toString())
    }

    await this.updateElements(variantId, incomingProductMarkup)

    this.isUpdating = false

    return variantId
  }

  selectVariant(id) {
    // Bound here rather than calling super inside the arrow below: Babel's legacy
    // build can't transform `super` in an arrow function without also compiling classes
    const selectVariant = super.selectVariant.bind(this)

    // Move execution to end of queue to allow for product handle update prior to variant selection
    setTimeout(async () => {
      // If variant product has changed, handle it, otherwise pass to vanilla variant switcher
      if (this.product.handle !== this.productHandle) {
        await this.updateVariantProduct(id)
        return
      }

      if (!id) return

      selectVariant(id)
    })
  }
}

if (!window.customElements.get('product-variant-picker')) {
  window.customElements.define('product-variant-picker', ProductVariantPicker)
}


/**
 * A product swatches component. Handles swatch generation and logic for PDPs.
 *
 * @extends GWElement
 *
 * @todo: Test with size-less products
 */
export class ProductSwatches extends GWElement {
  constructor() {
    super()

    this.variantPicker = this.closest('.variant-picker')
    this.fieldset = this.closest('fieldset')
    this.sizeFieldset = this.variantPicker.querySelector('fieldset[data-option="size"]')

    this.renderSwatches()

    this.addListener(
      this.fieldset,
      'change',
      this.onChange,
    )

    if (this.variantPicker) {
      this.addListener(
        this.variantPicker,
        'product:update:start',
        this.onProductUpdateStart,
      )

      this.addListener(
        this.variantPicker,
        'product:update:end',
        this.onProductUpdateEnd,
      )
    }

    if (this.sizeFieldset) {
      this.addListener(
        this.sizeFieldset,
        'change',
        this.onSizeChange,
      )
    }
  }

  onProductUpdateStart() {
    this.swatches?.forEach(swatch => swatch.setAttribute('aria-disabled', true))
  }

  onProductUpdateEnd() {
    this.swatches?.forEach(swatch => swatch.removeAttribute('aria-disabled'))
  }

  get name() {
    return this.getAttribute('input-name')
  }

  get idPrefix() {
    return this.getAttribute('input-id-prefix')
  }

  get color() {
    return this.getAttribute('current-color')
  }

  set color(value) {
    this.setAttribute('current-color', value)

    if (this.variantPicker) {
      this.variantPicker.setAttribute('handle', this.product.handle)
      this.variantPicker.querySelector('[data-option="color"] variant-option-value').textContent = this.color
    }
  }

  get size() {
    return this.getAttribute('current-size')
  }

  set size(value) {
    this.setAttribute('current-size', value)
  }

  get form() {
    return this.getAttribute('form')
  }

  get productTitle() {
    return this.getAttribute('product-title')
  }

  get product() {
    return this.products[this.color]
  }

  get variant() {
    return this.getMatchingVariant()
  }

  get variantId() {
    return this.getMatchingVariantId()
  }

  get queryBySale() {
    return parseAttribute(this.getAttribute('query-by-sale'))
  }

  // Per-product opt-out via the custom.group_swatches metafield. Can only turn
  // grouping off; the global theme setting still has to be enabled.
  get groupSwatches() {
    return parseAttribute(this.getAttribute('group-swatches')) !== false
  }

  get swatches() {
    return [...this.querySelectorAll('.product-swatch input')]
  }

  get listbox() {
    return document.querySelector(`product-swatch-listbox[form="${this.form}"][input-name="${this.name}"]`)
  }

  getMatchingVariant(product = this.product) {
    return product.variants.find(variant => variant.selectedOptions.every(
      ({ name, value }) => this[handle(name)] === value
    ))
  }

  getMatchingVariantId(product = this.product) {
    return parseGID(this.getMatchingVariant(product)?.id)
  }

  async renderSwatches() {
    const { productTitle, queryBySale, color, name, idPrefix } = this
    const grouped = !!window.themeVariables.settings.groupSwatchesByMetaobject && this.groupSwatches

    const [products] = await Promise.all([
      getSwatchProducts(productTitle, { queryBySale }),
      ...(grouped ? [getSwatchGroups()] : []),
    ])

    if (!products.length) return

    renderSwatches({
      products,
      name,
      current: color,
      container: this,
      idPrefix,
      grouped,
      // Sale flags are hidden when swatches are split by sale status
      // (query-by-sale set), since every visible swatch would share it.
      flags: window.themeVariables.settings.showSwatchFlags ? { sale: queryBySale === null } : false,
    })
    this.listbox?.renderSwatchOptions(products)

    this.products = products.reduce((acc, product) => ({ ...acc, [product.swatch.name]: product }), {})

    // TODO: Check pagespeed impact and adjust trigger if needed
    if (this.variantPicker) {
      window.requestIdleCallback(() => {
        products.forEach(product => this.variantPicker.getVariantProduct(product, false))
      })
    }
  }

  onChange(event) {
    event.preventDefault()
    event.stopPropagation()
    this.color = event.target.value
  }

  onSizeChange(event) {
    this.size = event.target.value
  }
}

if (!window.customElements.get('product-swatches')) {
  window.customElements.define('product-swatches', ProductSwatches)
}

export class ProductOptionListbox extends GWElement {
  constructor() {
    super()

    this.input = this.querySelector('input[type="hidden"]')
    this.baseOption = this.querySelector('[role="option"]')
    this.masterSelector = document.forms[this.getAttribute("form")].id
    this.variantPicker = document.querySelector(`.variant-picker[form="${this.getAttribute('form')}"]`)
    this.addListener(this, 'keydown', this.onKeyDown)
    this.addListener(this.input, 'change', this.onInputChange)
    this.addListener(this.masterSelector, 'change', this.onVariantChange)
    this.addDelegatedListener(this, '[role="option"]', 'click', this.onOptionClick)
    this.addAttributeListener('aria-activedescendant', this.onActiveDescendantChange)
  }

  init() {
    super.init()
    this.setAttribute('role', 'listbox')

    this.initOptions()
  }

  static get observedAttributes() {
    return ['aria-activedescendant']
  }

  get popover() {
    return this.closest('x-popover')
  }

  get name() {
    return this.getAttribute('input-name')
  }

  get option() {
    return this.getAttribute('option')
  }

  get optionEls() {
    return [...this.querySelectorAll('[role="option"]')]
  }

  get selected() {
    return this.input?.value
  }

  set selected(value) {
    this.input.value = value
    this.input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  get formId() {
    return this.getAttribute('form')
  }

  initOptions() {
    this.optionEls.forEach(this.initOption.bind(this))
  }

  initOption(option) {
    if (option.hasAttribute('initialized')) return

    option.id ||= this.constructor.generateOptionId()

    if (option.getAttribute('aria-selected') === 'true') {
      this.setAttribute('aria-activedescendant', option.id)
    }

    option.setAttribute('initialized', '')
  }

  static generateOptionId() {
    return 'option-' + (crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4))
  }

  onInputChange(event) {
    this.setAttribute('aria-activedescendant', this.querySelector(`[role="option"][value="${CSS.escape(event.target.value)}"]`).id)
  }

  onActiveDescendantChange(newValue, oldValue) {
    if (oldValue !== null && newValue !== oldValue) {
      this.optionEls.forEach(option => {
        if (option.id === newValue) {
          option.setAttribute('aria-selected', 'true')

          if (this.selected !== option.value) {
            this.input.value = option.value
            this.input.dispatchEvent(new Event('change', { bubbles: true }))
          }

          if (this.hasAttribute('aria-owns')) {
            this.getAttribute('aria-owns').split(" ").forEach(boundId => {
              const selectionEl = document.getElementById(boundId)
              const optionSwatch = option.querySelector('[data-swatch]')

              if (optionSwatch) {
                const newSwatch = optionSwatch.cloneNode(true)
                selectionEl.querySelector('[data-swatch]').replaceWith(newSwatch);
              }

              selectionEl.querySelector('[data-label]').textContent = option.getAttribute("title") || option.innerText || option.value
            })
          }

          option.dispatchEvent(new CustomEvent('option:listbox:change', {
            bubbles: true,
            detail: {
              name: this.name,
              value: option.value,
            }
          }));
        } else {
          option.setAttribute('aria-selected', 'false');
        }
      });
    }
  }

  sendSelection(option) {
    const { value } = option

    const fieldsets = document.querySelectorAll(`fieldset[data-form="${this.formId}"][data-option="${this.option}"]`)
    fieldsets.forEach(fieldset => {
      fieldset.querySelectorAll('[type="radio"], [type="checkbox"]').forEach(input => {
        const triggerChangeEvent = !input.checked && input.value === value
        input.checked = input.value === value

        if (triggerChangeEvent) {
          input.dispatchEvent(new Event('change', { bubbles: true }))
        }
      })
    })

    option.dispatchEvent(
      new CustomEvent('option:select', {
        bubbles: true,
        detail: {
          name: this.name,
          value: value,
        },
      }),
    )
  }

  onOptionClick(event, target) {
    if (target.getAttribute('type') === 'submit') return
    this.setAttribute('aria-activedescendant', target.id)
    this.sendSelection(target)
    this.popover.hide()
  }

  // TODO: FIX THIS ONCE ORDER OF ELS IS FIXED
  onKeyDown(event) {
    if (event.key === "ArrowUp") {
      event.target.previousElementSibling?.focus();
      event.preventDefault();
    } else if (event.key === "ArrowDown") {
      event.target.nextElementSibling?.focus();
      event.preventDefault();
    }
  }

  onVariantChange(event) {
    if (!this.variantPicker) {
      console.error('Failed to update product option listbox availability: Variant picker not found')
      return
    }

    const { value: variantId } = event.currentTarget
    const selectedVariant = this.variantPicker.selectedVariant
    let currentValue
    const selectedOptions = {}
    for (const fieldset of [...this.variantPicker.querySelectorAll('fieldset')]) {
      const name = fieldset.dataset.name
      const value = [...fieldset.querySelectorAll(`input[name="${name}"]`)].find(input => input.checked)?.value

      if (name === this.name) {
        currentValue = value
      } else {
        selectedOptions[name.split('-').shift()] = value
      }
    }

    if (currentValue !== this.selected) {
      this.selected = currentValue
    }

    this.updateAvailability(selectedOptions)
  }

  updateAvailability(selectedOptions) {
    const product = this.variantPicker.product

    this.optionEls.forEach(option => {
      const options = {
        ...selectedOptions,
        [this.name.split('-').shift()]: option.value,
      }

      const variant = product.variants.find(variant => Object.entries(options).every(([key, value]) => variant[key] === value))

      if (!variant) {
        console.error(`Variant not found for options: ${JSON.stringify(options)}`)
        return
      }

      const { available } = variant

      option.toggleAttribute('disabled', !available)
      option.toggleAttribute('aria-disabled', !available)
      option.classList.toggle('is-disabled', !available)
    })
  }
}

if (!window.customElements.get('product-option-listbox')) {
  window.customElements.define('product-option-listbox', ProductOptionListbox)
}

export class ProductSwatchListbox extends ProductOptionListbox {
  constructor() {
    super()

    if (this.variantPicker) {
      this.addListener(
        this.variantPicker,
        'product:update:start',
        this.disable,
      )

      this.addListener(
        this.variantPicker,
        'product:update:end',
        this.enable,
      )
    }
  }
  renderSwatchOption(product) {
    const colorName = product.title.split(' - ').pop().trim()
    if (!product.availableForSale || this.baseOption.value === colorName) return

    const { swatch: { color, image } } = product
    const background = image ? `url(${image}&width=95)` : `linear-gradient(to right, ${color}, ${color})`
    // const order = (colorName.toLowerCase() == "black" ? 0 : ((10000 - product.totalInventory) < 1 ? 1 : (10000 - product.totalInventory)))

    const attributes = {
      value: colorName,
      title: colorName,
      'aria-selected': false,
      // style: `order: ${order};`,
    }
    const optionEl = this.baseOption.cloneNode(true)
    const optionSwatch = optionEl.querySelector('.color-swatch')
    for (const [key, value] of Object.entries(attributes)) optionEl.setAttribute(key, value)
    optionSwatch.style.setProperty('--swatch-background', background)
    optionSwatch.style.setProperty('--swatch-background-fallback', color)
    optionSwatch.querySelector('.sr-only').textContent = window.themeVariables.strings.swatch.replace('[color]', colorName)
    optionEl.querySelector('[data-label]').textContent = colorName
    optionEl.id = this.constructor.generateOptionId()
    optionEl.setAttribute('initialized', '')

    if (colorName.toLowerCase() === "black") {
      this.input.insertAdjacentElement('afterend', optionEl)
    } else {
      this.appendChild(optionEl)
    }
  }

  renderSwatchOptions(products) {
    for (const product of products) this.renderSwatchOption(product)
  }

  disable() {
    this.disabled = true
    this.optionEls.forEach(option => option.setAttribute('disabled', ''))
  }

  enable() {
    this.disabled = false
    this.optionEls.forEach(option => option.removeAttribute('disabled'))
  }

  updateAvailability() { }
}

if (!window.customElements.get('product-swatch-listbox')) {
  window.customElements.define('product-swatch-listbox', ProductSwatchListbox)
}

export class SwatchProductStickyBar extends ProductStickyBar {
  isPropagating = false

  constructor() {
    super()
    this.form = this.getAttribute('form')
    this.quantitySelectors = [...document.querySelectorAll(`quantity-selector[form="${this.form}"]`)]
    this.quantityInputs = this.quantitySelectors.map(selector => selector.querySelector('input[is="quantity-input"]'))
    this.handleQuantityChange = this.onQuantityChange.bind(this)
    this.handleQuantityStep = this.onQuantityStep.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.quantitySelectors.forEach(quantitySelector => {
      quantitySelector.querySelector('input[is="quantity-input"]').addEventListener('input', this.handleQuantityChange)
      quantitySelector.querySelectorAll('button').forEach(button => button.addEventListener('click', this.handleQuantityStep))
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.quantitySelectors.forEach(quantitySelector => {
      quantitySelector.querySelector('input[is="quantity-input"]').removeEventListener('input', this.handleQuantityChange)
      quantitySelector.querySelectorAll('button').forEach(button => button.removeEventListener('click', this.handleQuantityStep))
    })
  }

  onQuantityStep(event) {
    this.propagate(() => {
      const selector = event.currentTarget.closest('quantity-selector')
      const step = Number(event.currentTarget.dataset.step)

      this.quantitySelectors.forEach(quantitySelector => {
        if (quantitySelector === selector) return

        quantitySelector.querySelector(`button[data-step="${step}"]`).click()
      })
    })
  }

  onQuantityChange(event) {
    this.propagate(() =>
      this.quantityInputs.forEach(input => {
        if (input === event.target) return

        input.value = event.target.value
      }),
    )
  }

  propagate(callback) {
    if (this.isPropagating) return

    this.isPropagating = true
    callback()
    this.isPropagating = false
  }
}

if (!window.customElements.get('swatch-product-bar')) {
  window.customElements.define('swatch-product-bar', SwatchProductStickyBar)
}